/**
 * Poseidon dashboard — Live market and signal monitor (§3.2 C).
 *
 * Quotes with per-symbol age and spread, a configurable bar view, indicator
 * values, strategy markers, and the explainable signal breakdown. Candles are a
 * deliberate placeholder — see the note in the panel.
 */
import { useMemo, useState } from 'react';
import type { BarView, IndicatorSeriesView, QuoteRowView, SignalRow, StrategyMarkerView, Timeframe } from '../api/types';
import { POLL_INTERVALS } from '../config';
import { useApiData } from '../hooks/useApiData';
import { useDashboardEvent, useServices } from '../state/Services';
import { DataTable } from '../components/DataTable';
import type { TableColumn } from '../components/DataTable';
import { MarketChartPlaceholder } from '../components/charts';
import { SignalBreakdownPanel } from '../components/SignalBreakdownPanel';
import { StalenessBadge } from '../components/Staleness';
import { Badge, ErrorState, LoadingState, Panel, Unavailable } from '../components/primitives';
import { RESEARCH_STATUS_LABELS, TIMEFRAME_LABELS, toneForOutcome } from '../lib/labels';
import type { Tone } from '../lib/staleness';
import { formatDuration, formatNumber, formatPrice } from '../lib/format';

const TIMEFRAMES: readonly Timeframe[] = ['M1', 'M5', 'M15'];

const INDICATOR_LABELS: Record<IndicatorSeriesView['indicator'], string> = {
  vwap: 'VWAP (session)',
  atr14: 'ATR(14)',
  sma20: 'SMA(20)',
  ema50: 'EMA(50)',
  session_high: 'Session high',
  session_low: 'Session low',
};

export function MarketMonitor(): JSX.Element {
  const { api, isMock } = useServices();
  const [symbol, setSymbol] = useState<string>('EURUSD');
  const [timeframe, setTimeframe] = useState<Timeframe>('M5');
  const [selectedDecision, setSelectedDecision] = useState<string | null>(null);

  const quotes = useApiData('market-quotes', () => api.getQuotes(), { pollMs: POLL_INTERVALS.quotes, mode: api.mode });
  const bars = useApiData(`market-bars-${symbol}-${timeframe}`, () => api.getBars(symbol, timeframe, 120), {
    pollMs: POLL_INTERVALS.quotes,
    mode: api.mode,
  });
  const indicators = useApiData(`market-indicators-${symbol}-${timeframe}`, () => api.getIndicators(symbol, timeframe), {
    pollMs: POLL_INTERVALS.ribbon,
    mode: api.mode,
  });
  const markers = useApiData(`market-markers-${symbol}`, () => api.getMarkers(symbol), { pollMs: POLL_INTERVALS.signals, mode: api.mode });
  const signals = useApiData('market-signals', () => api.getSignals(), { pollMs: POLL_INTERVALS.signals, mode: api.mode });

  useDashboardEvent('quote.updated', () => quotes.refresh());

  const activeDecision = selectedDecision ?? signals.data?.find((candidate) => candidate.symbol === symbol)?.decision_id ?? signals.data?.[0]?.decision_id ?? null;
  const breakdown = useApiData(`market-breakdown-${activeDecision ?? 'none'}`, () => api.getSignalBreakdown(activeDecision ?? ''), {
    enabled: activeDecision !== null,
    mode: api.mode,
  });

  const latestBars = useMemo(() => bars.data ?? [], [bars.data]);
  const lastCompleteBar: BarView | null = useMemo(() => {
    for (let index = latestBars.length - 1; index >= 0; index -= 1) {
      const bar = latestBars[index];
      if (bar !== undefined && bar.complete) return bar;
    }
    return null;
  }, [latestBars]);

  const quoteColumns: readonly TableColumn<QuoteRowView, 'symbol' | 'bid' | 'ask' | 'spread' | 'age'>[] = [
    { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => <strong>{row.symbol}</strong> },
    { key: 'bid', header: 'Bid', align: 'right', sortValue: (row) => row.bid, render: (row) => <span className="text-mono">{formatPrice(row.bid)}</span> },
    { key: 'ask', header: 'Ask', align: 'right', sortValue: (row) => row.ask, render: (row) => <span className="text-mono">{formatPrice(row.ask)}</span> },
    {
      key: 'spread',
      header: 'Spread',
      align: 'right',
      sortValue: (row) => row.spread_points,
      render: (row) => (
        <span className="text-mono">
          {row.spread_pips === null ? '—' : `${formatNumber(row.spread_pips, 1)} pips`} ({row.spread_points} pts)
        </span>
      ),
    },
    {
      key: 'age',
      header: 'Quote age',
      sortValue: (row) => row.quote_age_ms,
      render: (row) => (
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span className="text-mono">{formatDuration(row.quote_age_ms)}</span>
          {/* A stale or over-cap quote is labelled here, per symbol. */}
          {row.stale ? <Badge tone="danger">STALE</Badge> : <StalenessBadge lastAt={row.last_tick_at} thresholds={{ aging: 2_000, stale: 5_000 }} />}
        </span>
      ),
    },
  ];

  const markerColumns: readonly TableColumn<StrategyMarkerView, 'time' | 'symbol' | 'side' | 'levels' | 'fill' | 'outcome'>[] = [
    { key: 'time', header: 'Signal time', sortValue: (row) => row.signal_time, render: (row) => <span className="text-mono">{new Date(row.signal_time).toLocaleString(undefined, { hour12: false })}</span> },
    { key: 'symbol', header: 'Symbol / side', sortValue: (row) => row.symbol, render: (row) => `${row.symbol} ${row.side}` },
    {
      key: 'levels',
      header: 'Proposed entry / stop / target',
      render: (row) => (
        <span className="text-mono">
          {formatPrice(row.proposed_entry)} / {formatPrice(row.stop_loss)} / {formatPrice(row.take_profit)}
        </span>
      ),
    },
    {
      key: 'fill',
      header: 'Actual fill',
      render: (row) =>
        row.actual_fill_price === null ? (
          <Unavailable reason="No fill recorded for this marker." />
        ) : (
          <span className="text-mono">
            {formatPrice(row.actual_fill_price)}
            {row.actual_fill_time !== null && <div className="text-faint">{new Date(row.actual_fill_time).toLocaleTimeString(undefined, { hour12: false })}</div>}
          </span>
        ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      sortValue: (row) => row.outcome,
      render: (row) => (row.outcome === null ? <Unavailable /> : <Badge tone={toneForOutcome(row.outcome)}>{row.outcome}</Badge>),
    },
  ];

  const indicatorRows = (indicators.data ?? []).map((series) => ({
    indicator: series.indicator,
    version: series.version,
    latest: series.points.at(-1)?.value ?? null,
    points: series.points.length,
  }));

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Live market &amp; signal monitor</h1>
          <div className="page-header__spec">§3.2 C · bars, volume/VWAP/ATR/MA, strategy markers, per-symbol quote age and the signal breakdown</div>
        </div>
        <div className="field-row">
          <label>
            Symbol
            <select value={symbol} onChange={(event) => setSymbol(event.target.value)}>
              {(quotes.data ?? []).map((quote) => (
                <option key={quote.symbol} value={quote.symbol}>
                  {quote.symbol}
                </option>
              ))}
              {(quotes.data ?? []).length === 0 && <option value={symbol}>{symbol}</option>}
            </select>
          </label>
          <label>
            Timeframe
            <select value={timeframe} onChange={(event) => setTimeframe(event.target.value as Timeframe)}>
              {TIMEFRAMES.map((value) => (
                <option key={value} value={value}>
                  {TIMEFRAME_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <Panel title="Quotes" subtitle="per-symbol quote age and spread (§3.2 C, §7.1 staleness gate)" mock={isMock} flush>
        {quotes.error !== null && quotes.data === null ? (
          <ErrorState error={quotes.error} context="Quotes could not be loaded." onRetry={quotes.refresh} />
        ) : (
          <DataTable
            rows={quotes.data ?? []}
            columns={quoteColumns}
            rowKey={(row) => row.symbol}
            initialSort={{ key: 'symbol', direction: 'asc' }}
            emptyTitle="No quotes"
            rowTone={(row): Tone | null => (row.stale ? 'danger' : null)}
          />
        )}
      </Panel>

      <Panel
        title={`Price chart — ${symbol} ${timeframe}`}
        subtitle={lastCompleteBar === null ? 'no completed bar' : `last complete bar ${new Date(lastCompleteBar.time).toLocaleString(undefined, { hour12: false })}`}
        mock={isMock}
      >
        <div className="grid" style={{ gap: 12 }}>
          <MarketChartPlaceholder
            symbol={symbol}
            timeframe={timeframe}
            barCount={latestBars.filter((bar) => bar.complete).length}
            overlays={['VWAP', 'ATR(14)', 'SMA(20)', 'session high/low', 'strategy markers (signal, entry, stop, target, fill)']}
          />
          <div className="data-clock">
            <span className="text-faint">bars loaded</span>
            <span className="text-mono">{latestBars.length}</span>
            <span className="text-faint">forming bar</span>
            <span className="text-mono">{latestBars.some((bar) => !bar.complete) ? 'yes — excluded from any signal calculation' : 'none'}</span>
            {lastCompleteBar !== null && (
              <>
                <span className="text-faint">last close</span>
                <span className="text-mono">{formatPrice(lastCompleteBar.close)}</span>
                <span className="text-faint">volume</span>
                <span className="text-mono">{formatNumber(lastCompleteBar.tick_volume, 0)}</span>
              </>
            )}
          </div>
        </div>
      </Panel>

      <div className="grid grid--2">
        <Panel title="Indicators and overlays" subtitle="values from the daemon, never recomputed here" mock={isMock} flush>
          {indicators.data === null ? (
            <LoadingState label="Loading indicators…" />
          ) : (
            <DataTable
              rows={indicatorRows}
              rowKey={(row) => `${row.indicator}-${row.version}`}
              columns={[
                { key: 'indicator', header: 'Indicator', sortValue: (row) => row.indicator, render: (row) => INDICATOR_LABELS[row.indicator] },
                { key: 'latest', header: 'Latest', align: 'right', sortValue: (row) => row.latest, render: (row) => (row.latest === null ? <Unavailable /> : <span className="text-mono">{formatPrice(row.latest)}</span>) },
                { key: 'points', header: 'Points', align: 'right', sortValue: (row) => row.points, render: (row) => row.points },
                { key: 'version', header: 'Version', render: (row) => <span className="text-mono">{row.version}</span> },
              ]}
              emptyTitle="No indicator series"
            />
          )}
        </Panel>

        <Panel title="Active signals" subtitle="pick one to explain it below" mock={isMock} flush>
          <DataTable
            rows={signals.data ?? []}
            rowKey={(row) => row.decision_id}
            onRowClick={(row: SignalRow) => setSelectedDecision(row.decision_id)}
            initialSort={{ key: 'score', direction: 'desc' }}
            columns={[
              { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => `${row.symbol} ${row.side}` },
              { key: 'score', header: 'Final score', align: 'right', sortValue: (row) => row.final_score, render: (row) => (row.final_score === null ? <Unavailable /> : formatNumber(row.final_score, 3)) },
              { key: 'research', header: 'Research', sortValue: (row) => row.research_status, render: (row) => RESEARCH_STATUS_LABELS[row.research_status] },
              { key: 'status', header: 'Status', sortValue: (row) => row.status, render: (row) => row.status.replace(/_/g, ' ') },
            ]}
            emptyTitle="No active signals"
          />
        </Panel>
      </div>

      <Panel title={`Strategy markers — ${symbol}`} subtitle="signal time, proposed entry/stop/target and the actual fill" mock={isMock} flush>
        <DataTable rows={markers.data ?? []} columns={markerColumns} rowKey={(row) => row.marker_id} initialSort={{ key: 'time', direction: 'desc' }} emptyTitle="No markers for this symbol" />
      </Panel>

      {activeDecision === null ? (
        <Panel title="Explainable signal breakdown">
          <p className="text-muted">No signal is available to explain yet.</p>
        </Panel>
      ) : breakdown.error !== null && breakdown.data === null ? (
        <ErrorState error={breakdown.error} context={`The breakdown for decision ${activeDecision} could not be loaded.`} onRetry={breakdown.refresh} />
      ) : breakdown.data === null ? (
        <LoadingState label="Loading signal breakdown…" />
      ) : (
        <SignalBreakdownPanel breakdown={breakdown.data} mock={isMock} />
      )}
    </>
  );
}
